/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable no-use-before-define */
import React, { useState, useContext, useCallback, useRef } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Columns, SelectedColumns } from '@bingads-webui-component-legacy/bingads-grid/service/column-chooser-preferences';
import { assertIsDefined, assert } from '@bingads-webui-campaign-react/debug-assert';
import { I18n } from '@bingads-webui-grid/well-known';
import { ToolbarColumnChooserProps, ToolbarColumnChooserWrapper } from './ColumnChooser';
import { RemoveDialog } from './RemoveDialog';

const dropdownId = 'dropdown-column-chooser';
const saveListId = 'dropdown-radio-group-saved-columns';
const defaultColumnSetName = 'Custom';

type HookColumnChooserProps = Omit<ToolbarColumnChooserProps, 'onApply' | 'gridName'> & {
  onApply: (newColumnKeys: string[]) => void;
}

/**
 * All opeions should never change
 */
interface UseColumnsWithToolbarColumnChooserOptions {
  defaultColumns: string[],
  gridName: string,
  toolbarOptions: {
    toolbarEditor: any,
    /**
     * Used to open column chooser when user click "Modify columns"
     */
    openColumnChooser: () => void,
  },
  callbacks?: {
    beforeSetSelectedColumns?: () => void;
  },
  customToolbarIcon?: string,
}

/**
 * This hook handle many things
 * - Preference
 * - initial toolbar for column chooser
 * - update toolbar when choose new columns
 * - Dialog when click "remove" button to remove column set
 */
export function useColumnsWithToolbarColumnChooser(options: UseColumnsWithToolbarColumnChooserOptions) {
  const {
    defaultColumns, gridName, toolbarOptions, callbacks, customToolbarIcon,
  } = options;
  const i18n = useContext(I18nContext) as I18n;

  const { toolbarEditor } = toolbarOptions;

  const [selectedColumns, setSelectedColumns] = useState<string[]>(defaultColumns);

  const isInitialized = useRef(false);
  const initialColumns = useCallback(async () => {
    assert(isInitialized.current === false, 'You should only initial column chooser for once!');
    isInitialized.current = true;

    await prepareData(gridName);

    const selectedColumnSetName = getPreferenceSelectedColumnSetName({ gridName });

    // Not support custom columns for now
    // Not handle lagacy for now
    handleSelectColumnSet({
      callbacks,
      columnSetName: selectedColumnSetName,
      defaultColumns,
      gridName,
      setSelectedColumns,
    });
  }, [callbacks, defaultColumns, gridName]);

  initialColumnChooserItemInToolbar({
    customToolbarIcon, gridName, i18n, toolbarEditor,
  });

  const [isRemoveDialogOpened, setIsRemoveDialogOpened] = useState(false);
  const [columnSetNameRemoveCandidate, setColumnSetNameRemoveCandidate] = useState<string>();

  function onRemoveDismiss() {
    setIsRemoveDialogOpened(false);
  }

  async function onRemoveApply() {
    setIsRemoveDialogOpened(false);

    const promises = [];
    promises.push(deletePreferenceSelectedColumns({ columnSetName: columnSetNameRemoveCandidate, gridName }));
    if (columnSetNameRemoveCandidate === toolbarEditor.get(saveListId).value) {
      promises.push(handleSelectColumnSet({
        columnSetName: defaultColumnSetName,
        callbacks,
        defaultColumns,
        gridName,
        setSelectedColumns,
      }));
    }
    await Promise.all(promises);
    handleRefreshMenu({ gridName, i18n, toolbarEditor });
  }

  function ColumnChooserTrivia() {
    return (<RemoveDialog
      hidden={!isRemoveDialogOpened}
      columnSetName={columnSetNameRemoveCandidate}
      i18n={i18n}
      onDismiss={onRemoveDismiss}
      onApply={onRemoveApply}
    />);
  }

  function ColumnChooser(props: HookColumnChooserProps) {
    const { onApply } = props;
    return (
      <ToolbarColumnChooserWrapper
        {...props}
        gridName={gridName}
        onApply={async (n) => {
          const { selectedKeys, savedName } = n;
          onApply(selectedKeys);

          const columnSetName = savedName || defaultColumnSetName;
          setSelectedColumns(selectedKeys);

          await Promise.all([
            setPreferenceSelectedColumnSet({ columnSetName, gridName }),
            setPreferenceSelectedColumns({ columns: selectedKeys, columnSetName, gridName }),
          ]);

          handleRefreshMenu({ gridName, i18n, toolbarEditor });
        }}
      />
    );
  }

  return {
    /**
     * This property must be called once.
     *
     * Initialization is not done in an useEffect, for user usually wants to send only one call after all initialization is done.
     *
     * Have to let user control data flow.
     */
    initialColumns,
    columns: selectedColumns,
    /**
     * Always render this
     */
    ColumnChooserTrivia,
    ColumnChooser,
  };

  function initialColumnChooserItemInToolbar({
    // eslint-disable-next-line @typescript-eslint/no-shadow
    toolbarEditor, customToolbarIcon, i18n, gridName,
  }) {
    toolbarEditor.update({
      iconLeftGlyphName: customToolbarIcon || 'ba-TripleColumnSolid',
      id: dropdownId,
      button: {
        text: i18n.getString(_TL_('Columns')),
        iconLeft: ['glyphicon', 'toolbar-icon', 'toolbar-icon-left', 'icon-columnchooser-normal'],
        iconRight: ['glyphicon', 'toolbar-icon', 'glyphicon-triangle-bottom'],
      },
      menu: {
        items: [{
          text: i18n.getString('Modify columns'),
          onClick: toolbarOptions.openColumnChooser,
        }, {
          type: 'dropdown-divider',
        }, {
          type: 'dropdown-radio-group',
          id: saveListId,
          title: i18n.getString(_TL_('Apply saved columns')),
          items: [],
          removeText: i18n.getString(_TL_('Remove')),
          // `onSelect` must be async, somewhere else call `then` on the result
          // no need to call `handleRefreshMenu` explicitly, which will be called after `onSelect` is called
          onSelect: async columnSetName => handleSelectColumnSet({
            columnSetName, callbacks, defaultColumns, gridName, setSelectedColumns,
          }),
          onRemove: (columnSetName) => {
            setIsRemoveDialogOpened(true);
            setColumnSetNameRemoveCandidate(columnSetName);
          },
          refreshMenu: () => { handleRefreshMenu({ toolbarEditor, gridName, i18n }); },
        }],
      },
    });
    handleRefreshMenu({ toolbarEditor, gridName, i18n });
  }
}

function handleRefreshMenu({ toolbarEditor, gridName, i18n }) {
  const items = Columns.filter({ where: buildBaseQuery(gridName) }).reduce((memo, { name }) => {
    if (name !== defaultColumnSetName) {
      memo.push({
        text: name,
        value: name,
      });
    }
    return memo;
  }, [{
    text: i18n.getString(_TL_('Custom')),
    value: defaultColumnSetName,
    locked: true,
  }]);

  const columnSetName = getPreferenceSelectedColumnSetName({ gridName });

  toolbarEditor.update({
    id: saveListId,
    items,
    value: columnSetName,
  });
}

async function handleSelectColumnSet({
  defaultColumns, columnSetName, gridName, callbacks, setSelectedColumns,
}) {
  const promises = [];
  const selectedColumnsPreference = await getPreferenceSelectedColumns({ columnSetName, gridName });
  if (selectedColumnsPreference) {
    let finalColumns = [...selectedColumnsPreference.columns];
    finalColumns = callbacks?.beforeSetSelectedColumns(finalColumns) || finalColumns;
    setSelectedColumns(finalColumns);
  } else {
    setSelectedColumns(defaultColumns);
    promises.push(setPreferenceSelectedColumns({ columns: defaultColumns, columnSetName, gridName }));
  }

  if (columnSetName !== getPreferenceSelectedColumnSetName({ gridName })) {
    promises.push(setPreferenceSelectedColumnSet({ columnSetName, gridName }));
  }

  return Promise.all(promises);
}

// Not know what this is really doing, just imitate existing behavior in
// "projection-grid-column-chooser\src\plugin.js"
async function prepareData(gridName) {
  const query = buildBaseQuery(gridName);
  return Promise.all([Columns.findAll({ where: query }), SelectedColumns.findAll({ where: query })]);
}

// Should not use this, but it's fine. I have not find any other proecjtName.
function buildBaseQuery(gridName, projectName = 'CampaignUI') {
  return {
    project: projectName,
    grid: gridName,
  };
}

function getPreferenceSelectedColumnSet({ gridName }) {
  const query = buildBaseQuery(gridName);
  return SelectedColumns.filter({ where: query })[0];
}

function getPreferenceSelectedColumnSetName({ gridName }) {
  const columnSetPreference = getPreferenceSelectedColumnSet({ gridName });
  return columnSetPreference?.name ?? defaultColumnSetName;
}

function setPreferenceSelectedColumnSet({ gridName, columnSetName }) {
  const columnSetPreference = getPreferenceSelectedColumnSet({ gridName });
  if (columnSetPreference) {
    return SelectedColumns.update(columnSetPreference.id, { name: columnSetName });
  }
  return SelectedColumns.create({ name: columnSetName, ...buildBaseQuery(gridName) });
}

function getPreferenceSelectedColumns({ gridName, columnSetName }) {
  return Columns.filter({
    where: { name: columnSetName, ...buildBaseQuery(gridName) },
  })[0];
}

async function setPreferenceSelectedColumns({ gridName, columnSetName, columns }) {
  const columnsPreference = getPreferenceSelectedColumns({ gridName, columnSetName });
  if (columnsPreference) {
    return Columns.update(columnsPreference.id, { columns });
  }
  return Columns.create({ columns, name: columnSetName, ...buildBaseQuery(gridName) });
}

async function deletePreferenceSelectedColumns({ gridName, columnSetName }) {
  const columnsPreference = getPreferenceSelectedColumns({ gridName, columnSetName });
  assertIsDefined(columnsPreference);
  return Columns.destroy(columnsPreference.id);
}
